import {openResourceSection} from '../../core/resourceEvents';
import {
	HISTORICAL_CLIENT_AP_SECTION_EVENT,
	HISTORICAL_CLIENT_FILTER_EVENT,
	HISTORICAL_CLIENT_HOME_SECTION_EVENT,
	HISTORICAL_CLIENT_SITE_SECTION_EVENT,
} from './events';

const HOME_SECTIONS = new Set(['home.clients_historical']);
const SITE_SECTIONS = new Set(['site.clients_historical']);
const AP_SECTIONS = new Set(['ap.clients_historical']);

/**
 * Open the Clients accordion on the Home Dashboard, on the Historical tab.
 * @return {Promise<void>} completion acknowledgement
 */
export function openHomeHistoricalSection() {
	return openResourceSection(
		HISTORICAL_CLIENT_HOME_SECTION_EVENT, 'home.clients_historical',
		HOME_SECTIONS,
	);
}

/**
 * Open the Clients accordion on a Site Dashboard, on the Historical tab.
 * @return {Promise<void>} completion acknowledgement
 */
export function openSiteHistoricalSection() {
	return openResourceSection(
		HISTORICAL_CLIENT_SITE_SECTION_EVENT, 'site.clients_historical',
		SITE_SECTIONS,
	);
}

/**
 * Open the Clients accordion on an Access Point Dashboard, on Historical.
 * @return {Promise<void>} completion acknowledgement
 */
export function openApHistoricalSection() {
	return openResourceSection(
		HISTORICAL_CLIENT_AP_SECTION_EVENT, 'ap.clients_historical',
		AP_SECTIONS,
	);
}

// The filter is the whole of this resource. Until it is applied the table
// says "Please set filter to see historical clients" and shows nothing, so
// arriving is never an answer here - unlike the live list, which is already
// populated when you get to it.
const ANSWER_TIMEOUT_MS = 10000;
const RETRY_MS = 120;

/**
 * Apply the Historical Clients filter.
 *
 * Site and Duration are both required by Canvas; the band and the Access
 * Point are optional and are simply left out when the request did not name
 * them. The values travel as Canvas's own codes - the duration as the
 * picker's index - because the catalogue holds the mapping and nothing here
 * should be translating English into it.
 *
 * @param {object} command apply_filter command
 * @return {Promise<void>} completion acknowledgement
 */
export function applyHistoricalFilter(command) {
	// Canvas's own parameter names, carrying what the request said. The Site
	// and the Access Point travel as names because apply_filter takes
	// {resource, query, ...narrowing} and nothing else - a resolved id has no
	// way through it - and turning a name into what Canvas stores is the
	// page's job, where the lookups already live.
	const params = command?.params || {};
	const detail = {
		siteName: params.site_name ?? null,
		apName: params.ap_name ?? null,
		duration: params.duration ?? null,
		radioType: params.radio_type ?? null,
	};
	return new Promise((resolve, reject) => {
		let settled = false;
		let retry = null;
		const finish = (callback) => {
			if (settled) return;
			settled = true;
			clearTimeout(timer);
			if (retry !== null) clearInterval(retry);
			callback();
		};
		const timer = setTimeout(
			() => finish(() => reject(new Error('historical_filter_unavailable'))),
			ANSWER_TIMEOUT_MS,
		);
		const send = () => {
			if (settled) return;
			window.dispatchEvent(new CustomEvent(HISTORICAL_CLIENT_FILTER_EVENT, {
				detail: {
					...detail,
					complete: () => finish(resolve),
					fail: (reason) => finish(
						() => reject(new Error(reason || 'historical_filter_failed')),
					),
				},
			}));
		};
		send();
		if (!settled) retry = setInterval(send, RETRY_MS);
	});
}
